import angular from "angular";

import { CalcRowConfig, CalculationRowCategory, CalculationRowType, CalculatorLogic, InputEnumOption, Objects, Translations, UserDefinedRow } from "@salaxy/core";

import { RowTypeService, SessionService, UiHelpers } from "../../services";

/**
 * UI logic for changing the row type and potentially the kind for UserDefined row in calculation.
 * Note that changing the type may change the message of the row as well:
 * In the future, usecase defaults may also be changed.
 */
export class CalcRowTypeController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SessionService", "UiHelpers", "RowTypeService"];

  /** Type of the input element. Default is typeahead. */
  public type: "typehead" | "list" | "read-only";

  /** If set, filters the rows based on categories (plus rowTypes if set) */
  public categories: CalculationRowCategory[];

  /** If set, shows only these types (plus categories if set) */
  public rowTypes: CalculationRowType[];

  /** Name and identifier of the typehead input */
  public name: string;

  /**
   * Placeholder text in the typeahead input.
   * Default is 'SALAXY.UI_Terms.select'.
   */
  public placeholder: string;

  /**
   * If true, the list will show all the children under each parent that is shown as a direct hit.
   */
  public showChildren: boolean;

  /**
   * Function that is called after the row type and potentially kind and message have been changed.
   * @example <salaxy-calc-row-type on-row-changed="$ctrl.commitNewRow()"></salaxy-calc-row-type>
   */
  public onRowChanged: (eventData: {
    /** New item which was created. */
    row: UserDefinedRow,
  }) => void;

  /** The calculation row that this component modifies. */
  public model: UserDefinedRow;

  /**
   * The default number of items that is returned from the typeahead search list.
   * Default is 50.
   */
  public top: number;

  private _internalValue: InputEnumOption;

  private modelRowType;

  private modelType;

  private modelDataKind;

  private listCache: {
    /** Cache key */
    key: string,
    /** Cache value */
    value: CalcRowConfig[],
  } = null;

  constructor(private session: SessionService, private uiHelpers: UiHelpers, private rowTypeService: RowTypeService) {
  }

  /** Sets the default values in init. */
  public $onInit() {
    this.top = this.top || 50;
    if (!this.type) {
      this.type = "typehead";
    }
  }

  /**
   * Detects the changes in the model properties "rowType" and "data.kind"
   * and reflects them to internalValue.
   */
  public $doCheck() {
    let hasChanges = false;
    if (this.model?.rowType !== this.modelRowType) {
      this.modelRowType = this.model?.rowType;
      hasChanges = true;
    }
    if (this.model?.data?.kind !== this.modelDataKind) {
      this.modelDataKind = this.model?.data?.kind;
      hasChanges = true;
    }
    if (this.model?.type !== this.modelType) {
      this.modelType = this.model?.type;
      hasChanges = true;
    }

    if (hasChanges) {
      const listRowType = this.modelType ? this.modelType.split("/")[2] : this.modelRowType;
      // NOTE: There is potential incoherent behavior here: depending on in which order attributes "categories"
      //       "rowTypes" and "model" are set, the filters may behave differently in edge case where model value
      //       is outside these filters. This was not considered significant at the time of writing.
      if (this.modelDataKind) {
        this._internalValue = this.getList().find((x) => x.value === `${listRowType}.${this.modelDataKind}`);
      } else {
        this._internalValue = this.getList().find((x) => x.value === (listRowType));
      }
    }
  }

  /**
   * Internal value from view to the controller. This is one of the numerations returned by getList()
   * if model has been set and it matches to oneof those values.
   */
  protected get internalValue(): InputEnumOption {
    return this._internalValue;
  }
  protected set internalValue(val: InputEnumOption) {
    this._internalValue = val;
    if (!this.model) {
      return;
    }
    this.model.data = this.model.data || {};
    if (!val) {
      this.model.rowType = null;
      this.model.data.kind = null;
      this.model.type = null;
    } else if (val.value.indexOf(".")) {
      const valueArr = val.value.split(".");
      this.model.rowType = valueArr[0] as CalculationRowType;
      this.model.data.kind = valueArr[1] || undefined;
      // TODO: Requires refactoring => type should be directly in value
      if (val.ui) {
        this.model.type = val.ui.type;
        this.model.rowType = val.ui.rowType;
        (this.model as any).config = {
          group: val.ui.group,
        };
      }
    } else {
      this.model.rowType = val.value as CalculationRowType;
      this.model.data.kind = undefined;
      // TODO: Requires refactoring => type should be directly in value
      if (val.ui) {
        this.model.type = val.ui.type;
        this.model.rowType = val.ui.rowType;
        (this.model as any).config = {
          group: val.ui.group,
        };
      }
    }
    this.onRowChanged({ row: this.model });
  }

  /**
   * Gets the list of row types according to options:
   * This list changes and is cached based on role (household/company), language,
   * rowTypes and categories. Currently the same as search(null), but cached.
   */
  public getList(): InputEnumOption[] {
    return this.uiHelpers.cache(this, "list",
      () => { return this.search(null, true, true); },
      () => Translations.getLanguage() + this.rowTypes?.join("") + this.categories?.join(""));
  }

  /**
   * Gets the search as cached item: Can be bound to ng-repeat.
   * @param searchText The search text. If null or empty, returns the full list.
   */
  public getCachedSearch(searchText: string): InputEnumOption[] {
    return this.uiHelpers.cache(this, "search",
      () => { return this.search(searchText, true, null); },
      () => searchText + this.categories?.toString());
  }

  /**
   * Searches the row types.
   * @param searchText The search text. If null or empty, returns the full list.
   * @param ignoreTop If true, ignores the this.top setting to return only top n items of the search
   * @param showChildren Forces the showChildren value to true for this run.
   * By default, children are only shown if they match the search.
   */
  public search = (searchText: string, ignoreTop = false, showChildren: boolean = null): InputEnumOption[] => {
    if (showChildren == null) {
      showChildren = this.showChildren;
    }
    const toOption = (config: CalcRowConfig) => {
      return {
        text: config.label,
        value: config.name,
        title: config.descr,
        ui: {
          color: config.color,
          iconText: config.iconText,
          addedByChild: null,
          initials: config.iconText,
          entityType: "company",
          // TODO: Requires refactoring
          group: config.group,
          type: config.type,
          rowType: config.rowType,
          isChild: false
        },
      };
    };
    const result: InputEnumOption[] = [];
    searchText = (searchText || "").trim().toLowerCase();
    this.getRowTypes().forEach((rowConfig) => {
      let isParentAdded = false;
      let isParentAddedAsSelf = false;
      if (
        !searchText
        || (rowConfig.label + rowConfig.descr + rowConfig.name).toLowerCase().indexOf(searchText) >= 0
      ) {
        result.push(toOption(rowConfig));
        if (!ignoreTop && result.length >= this.top) {
          return result;
        }
        isParentAdded = true;
        isParentAddedAsSelf = true;
      }
      if (rowConfig.kind?.values) {
        rowConfig.kind.values.forEach((kind) => {
          if (
            !(kind.name == "undefined" || kind.name == "unknown") &&
            (
              (isParentAddedAsSelf && showChildren)
              || (searchText && (kind.label + kind.descr + kind.name).toLowerCase().indexOf(searchText) >= 0)
            )
          ) {
            if (!isParentAdded) {
              const option = toOption(rowConfig);
              option.ui.addedByChild = true;
              result.push(option);
              isParentAdded = true;
            }
            result.push({
              text: kind.label,
              value: rowConfig.name + "." + kind.name,
              title: kind.descr,
              // TODO: Requires refactoring
              ui: {
                group: rowConfig.group,
                type: rowConfig.type,
                rowType: rowConfig.rowType,
                isChild: true
              }
            });
            if (!ignoreTop && result.length >= this.top) {
              return result;
            }
          }
        });
      }
    });

    if (result.length === 2 && result[0].ui.addedByChild) {
      // If only one parent with one child (child is exact match) => Merge to one row.
      return [{
        text: result[0].text + ": " + result[1].text,
        value: result[1].value,
        title: result[1].title,
        ui: result[0].ui,
      }];
    }

    return result;
  };

  /** Gets a label for the given internal value. */
  public getRowLabel(rowTypeKey: string) {
    if (!rowTypeKey) {
      return null;
    }
    let rowTypeName = null;
    let kindName = null;
    if (rowTypeKey.indexOf(".")) {
      const valueArr = rowTypeKey.split(".");
      rowTypeName = valueArr[0] as CalculationRowType;
      kindName = valueArr[1];
      if (kindName === "undefined") {
        kindName = null; // For undefined, take the main category.
      }
    } else {
      rowTypeName = rowTypeKey as CalculationRowType;
    }
    const rowType = this.getRowTypes().find((type) => type.name === rowTypeName);
    if (!rowType) {
      return null;
    }
    const kind = rowType.kind?.values?.find((x) => x.name === kindName);
    return kind ? kind.label : rowType.label;
  }

  /**
   * Shows a dilaog that contains information about the row type.
   * @param rowType The row type to show information about.
   */
  public showInfoScreen(rowType: InputEnumOption){
    this.model = this.model || {
      rowIndex: 0,
      data: {
        dimensions: {},
        accounting: {},
      },
    };
    this.internalValue = rowType; // Updates the model.
    this.uiHelpers.openEditDialog("salaxy-components/calc/rows/RowTypeEditDialog.html", this.model, {
      calc: CalculatorLogic.getBlank(),
    }, "lg", "CalcRows2019Controller", ).then((_result) => {
      // Do something?
    });

  }

  /**
   * Gets the list of row types. Method filters based on rowTypes and categories,
   * the texts are language versioned and there is a role filter for "household"/"company".
   */
  private getRowTypes(): CalcRowConfig[] {
    const key = "" + this.categories + this.rowTypes + this.rowTypeService.isReady;
    if (!this.listCache || this.listCache.key !== key) {
      const rowsLogic = this.rowTypeService.logic;
      let value;
      if ((this.categories || []).length + (this.rowTypes || []).length > 0) {
        const rowConfigsByCategory =  Objects.copy(rowsLogic.getRowConfigsByCategory(this.categories));
        // Remove kind from IrIncomeType: prevent listing of income types in category based lists
        const irIncomeType = rowConfigsByCategory.find( (x) => x.name === CalculationRowType.IrIncomeType);
        if (irIncomeType != null) {
          irIncomeType.kind = null;
        }
        value = rowsLogic.getRowConfigsByType(this.rowTypes).concat(rowConfigsByCategory);
      } else {
        value = rowsLogic.getRowConfigs();
      }

      // Obsolete
      const obsolete = [
        CalculationRowType.PaidSickLeaveSalary,
        CalculationRowType.PaidSickLeaveHourlySalary,
        CalculationRowType.PaidSickLeaveMonthlySalary,
        CalculationRowType.TrainingSalary,
        CalculationRowType.TrainingHourlySalary,
        CalculationRowType.TrainingMonthlySalary,
        CalculationRowType.ServiceCharge
      ];
      value = value.filter((x) => !(obsolete.indexOf(x.name) >= 0));

      // For pro and admin only
      if (!this.session.isInSomeRole("pro,admin")) {
        value = value.filter((x) => x.name != CalculationRowType.Service);
      }

      // For bizconfigs only
      if (!this.session.isInRole("bizConfigs")) {
        value = value.filter((x) => x.name != CalculationRowType.Script && x.name != CalculationRowType.PayStats);
      }

      this.listCache = { key, value };
    }
    return this.listCache.value;
  }
}
